import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { checkIfStoresCanDeliver } from '../api'
import type { Store } from '@/types'

interface UseGetDeliveryStoresParams {
  restaurantId: string
  destinationLat: number
  destinationLong: number
  sub1: string
  sub2: string
  enabled?: boolean
}

interface EnrichedStore extends Store {
  minOrderValue: string
  deliveryCost: string
  suburbId: string
}

/**
 * Haversine formula – returns distance in metres between two lat/long points.
 */
function haversineDistanceMetres(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371000 // Earth radius in metres
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export const useGetDeliveryStores = (params: UseGetDeliveryStoresParams) => {
  const storeConfig = useSelector((state: any) => state.store.store)

  const query = useQuery({
    queryKey: ['deliveryStores', params],
    queryFn: async () => {
      const response = await checkIfStoresCanDeliver({
        restaurantId: params.restaurantId,
        destinationLat: params.destinationLat,
        destinationLong: params.destinationLong,
        sub1: params.sub1,
        sub2: params.sub2,
      })

      if (response.code !== 200 || !response.data.deliveryInfo) {
        return []
      }

      const deliveryInfoMap = new Map(
        response.data.deliveryInfo.map((info) => [info.id, info])
      )

      const allStores = storeConfig?.stores || []

      const eligibleDeliveryStores: EnrichedStore[] = allStores
        .filter((store: any) => deliveryInfoMap.has(store.storeId))
        .map((store: any) => {
          const deliveryInfo = deliveryInfoMap.get(store.storeId)!

          // Calculate straight-line distance from delivery destination to the store
          const storeLat = store.addressLocation?.lat
          const storeLon = store.addressLocation?.long
          const distanceMetres =
            storeLat != null && storeLon != null
              ? haversineDistanceMetres(
                  params.destinationLat,
                  params.destinationLong,
                  storeLat,
                  storeLon,
                )
              : undefined

          return {
            ...store,
            id: store.id || store.storeId,
            minOrderValue: deliveryInfo.minOrderValue,
            deliveryCost: deliveryInfo.deliveryCost,
            suburbId: deliveryInfo.suburbId,
            distance: distanceMetres,
          }
        })

      // Sort by distance ascending (closest first)
      eligibleDeliveryStores.sort((a, b) => {
        if (a.distance == null) return 1
        if (b.distance == null) return -1
        return a.distance - b.distance
      })

      return eligibleDeliveryStores
    },
    enabled: params.enabled !== false,
  })

  return query
}
